'use client';

import React from 'react';
import { Typography } from '../../../../components';
import { IcDate, ClockIc } from '../../../../components/icons';
import { DateCellProps } from '../types';
import { useTranslations } from '../../../../contexts/translation.context';

const DateCell: React.FC<DateCellProps> = ({ bill }) => {
  const { lang } = useTranslations();

  const parseDate = (value?: string): Date | null => {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    const normalized = trimmed.replace(' ', 'T');
    const hasTimeZone = /([zZ]|[+\-]\d{2}:?\d{2})$/.test(normalized);

    if (hasTimeZone) {
      const parsed = new Date(normalized);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const [datePart, timePart = ''] = normalized.split('T');
    const [yearStr, monthStr, dayStr] = datePart.split('-');
    const [timeWithoutMs, msPart] = timePart.split('.');
    const [hourStr = '0', minuteStr = '0', secondStr = '0'] = (timeWithoutMs || '').split(':');

    const year = Number(yearStr);
    const monthIndex = Number(monthStr) - 1;
    const day = Number(dayStr);
    const hour = Number(hourStr);
    const minute = Number(minuteStr);
    const second = Number(secondStr);
    const millisecond = msPart ? Number(msPart.padEnd(3, '0').slice(0, 3)) : 0;

    if (
      [year, monthIndex, day, hour, minute, second, millisecond].some((val) => Number.isNaN(val))
    ) {
      return null;
    }

    const utcTimestamp = Date.UTC(year, monthIndex, day, hour, minute, second, millisecond);
    return new Date(utcTimestamp);
  };

  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (lang === 'ru') {
      if (diffDays > 0) return `${diffDays}д. назад`;
      if (diffHours > 0) return `${diffHours}ч. ${diffMins % 60}мин. назад`;
      if (diffMins > 0) return `${diffMins}мин. ${diffSecs % 60}сек. назад`;
      return 'только что';
    } else {
      if (diffDays > 0) return `${diffDays}d ago`;
      if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m ago`;
      if (diffMins > 0) return `${diffMins}m ${diffSecs % 60}s ago`;
      return 'just now';
    }
  };

  const date = parseDate(bill.created_at);
  const isValidDate = !!date;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <IcDate className="w-3.5 h-3.5 text-gray-600" />
        <Typography variant="body3" className="text-gray-600">
          {isValidDate ? getTimeAgo(date!) : '—'}
        </Typography>
      </div>
      <div className="flex items-center gap-1">
        <ClockIc />
        <Typography variant="body3" className="text-gray-600">
          {isValidDate
            ? date!.toLocaleDateString('ru-RU') +
              ' ' +
              date!.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
            : '—'}
        </Typography>
      </div>
    </div>
  );
};

export default DateCell;
